import {
  AppAccessStatus,
  AssignedSlotRangeResponse,
  DayOfWeek,
  PartnerAdminPractitionerTimeOffSummary,
  PartnerAdminPractitionerWorkingHourSummary,
  PartnerWorkingHour,
  PartnerServiceResponse,
  TimeRangeResponse,
  PractitionerAvailabilityResponse,
  Partner,
  PartnerAdministrator,
  ServicesReportResponse,
  ServiceReportItemResponse,
  PatientsWeeklyReportResponse,
  PatientReportServiceResponse,
  PatientReportDayResponse,
  PatientReportSlotResponse,
  PartnerPatientReportResponse,
  PartnerPatientAppointmentResponse,
  PartnerPatientSlotHistoryResponse,
  SlotStatusType,
  RescheduleSlotResponse,
  PartnerServiceSlotResponse,
  EmailTemplateResponse,
} from '../models'
import {AssertTypeFn} from './general'
import {
  assertArray,
  assertEnum,
  assertNumberOrNull,
  assertString,
  assertStringOrNull,
  assertArray as assertArr,
  assertString as asStr,
} from '../utils'

const assertTime = (value: any): any =>
  typeof value === 'string'
    ? value
    : {
        hour: assertNumberOrNull(value?.hour),
        minute: assertNumberOrNull(value?.minute),
        nano: assertNumberOrNull(value?.nano),
        second: assertNumberOrNull(value?.second),
      }

export const partnerWorkingHourNormaliser: AssertTypeFn<PartnerWorkingHour> = (
  obj,
) => ({
  id: assertString(obj.id),
  dayOfWeek: assertEnum(obj.dayOfWeek, DayOfWeek),
  startTime: assertTime(obj.startTime),
  endTime: assertTime(obj.endTime),
})

export const partnerWorkingHoursNormaliser: AssertTypeFn<
  PartnerWorkingHour[]
> = (arr) => assertArray(arr, partnerWorkingHourNormaliser)

export const partnerAdminPractitionerWorkingHourSummaryNormaliser: AssertTypeFn<
  PartnerAdminPractitionerWorkingHourSummary
> = (obj) => ({
  id: assertString(obj.id),
  practitionerId: assertString(obj.practitionerId),
  practitionerFirstName: assertString(obj.practitionerFirstName),
  practitionerSecondName: assertString(obj.practitionerSecondName),
  dayOfWeek: assertEnum(obj.dayOfWeek, DayOfWeek),
  startTime: assertTime(obj.startTime),
  endTime: assertTime(obj.endTime),
})

export const partnerAdminPractitionerWorkingHourSummariesNormaliser: AssertTypeFn<
  PartnerAdminPractitionerWorkingHourSummary[]
> = (arr) =>
  assertArray(arr, partnerAdminPractitionerWorkingHourSummaryNormaliser)

export const partnerAdminPractitionerTimeOffSummaryNormaliser: AssertTypeFn<
  PartnerAdminPractitionerTimeOffSummary
> = (obj) => ({
  id: assertString(obj.id),
  practitionerId: assertString(obj.practitionerId),
  practitionerFirstName: assertString(obj.practitionerFirstName),
  practitionerSecondName: assertString(obj.practitionerSecondName),
  startTime: assertString(obj.startTime),
  endTime: assertString(obj.endTime),
})

export const partnerAdminPractitionerTimeOffSummariesNormaliser: AssertTypeFn<
  PartnerAdminPractitionerTimeOffSummary[]
> = (arr) => assertArray(arr, partnerAdminPractitionerTimeOffSummaryNormaliser)

// --- Partner services ---

export const timeRangeNormaliser: AssertTypeFn<TimeRangeResponse> = (obj) => ({
  startTime: assertTime(obj.startTime),
  endTime: assertTime(obj.endTime),
})

export const timeRangesNormaliser: AssertTypeFn<TimeRangeResponse[]> = (arr) =>
  assertArray(arr, timeRangeNormaliser)

export const assignedSlotRangeNormaliser: AssertTypeFn<
  AssignedSlotRangeResponse
> = (obj) => ({
  startTime: assertTime(obj.startTime),
  endTime: assertTime(obj.endTime),
  serviceInternalCode: assertStringOrNull(obj.serviceInternalCode),
})

export const assignedSlotRangesNormaliser: AssertTypeFn<
  AssignedSlotRangeResponse[]
> = (arr) => assertArray(arr, assignedSlotRangeNormaliser)

export const practitionerAvailabilityNormaliser: AssertTypeFn<
  PractitionerAvailabilityResponse
> = (obj) => ({
  availabilityBasedOnPartnerWorkingHours: timeRangesNormaliser(
    obj.availabilityBasedOnPartnerWorkingHours,
  ),
  availabilityBasedOnStaffWorkingHours: timeRangesNormaliser(
    obj.availabilityBasedOnStaffWorkingHours,
  ),
  availabilityBasedOnTimeOff: timeRangesNormaliser(
    obj.availabilityBasedOnTimeOff,
  ),
  availabilityBasedSlotAlreadyAssigned: assignedSlotRangesNormaliser(
    obj.availabilityBasedSlotAlreadyAssigned,
  ),
  bookableTimeRanges: timeRangesNormaliser(obj.bookableTimeRanges),
})

export const partnerServiceNormaliser: AssertTypeFn<PartnerServiceResponse> = (
  obj,
) => ({
  id: assertString(obj.id),
  name: assertString(obj.name),
  appointmentDuration: obj.appointmentDuration,
  contacts: assertStringOrNull(obj.contacts),
  internalCode: assertStringOrNull(obj.internalCode),
  location: assertStringOrNull(obj.location),
  postQuestionnaireId: assertStringOrNull(obj.postQuestionnaireId),
  preQuestionnaireId: assertStringOrNull(obj.preQuestionnaireId),
  attachmentFileId: assertStringOrNull(obj.attachmentFileId),
  startDate: assertStringOrNull(obj.startDate),
  endDate: assertStringOrNull(obj.endDate),
})

export const partnerServicesNormaliser: AssertTypeFn<
  PartnerServiceResponse[]
> = (arr) => assertArray(arr, partnerServiceNormaliser)

// --- Partner Administrator & Partner ---

export const partnerNormaliser: AssertTypeFn<Partner> = (obj) => ({
  id: assertString(obj.id),
  facilityId: assertString(obj.facilityId),
  name: assertString(obj.name),
  timeZone: assertString(obj.timeZone),
  address: assertStringOrNull(obj.address),
  email: assertStringOrNull(obj.email),
  logoUrl: assertStringOrNull(obj.logoUrl),
  phone: assertStringOrNull(obj.phone),
  privacyPolicyUrl: assertStringOrNull(obj.privacyPolicyUrl),
  termsAndConditionsUrl: assertStringOrNull(obj.termsAndConditionsUrl),
  website: assertStringOrNull(obj.website),
})

export const partnerAdministratorNormaliser: AssertTypeFn<
  PartnerAdministrator
> = (obj) => ({
  id: assertString(obj.id),
  email: assertString(obj.email),
  firstName: assertString(obj.firstName),
  secondName: assertString(obj.secondName),
  partner: partnerNormaliser(obj.partner),
})

const serviceReportItemNormaliser: AssertTypeFn<ServiceReportItemResponse> = (
  obj,
) => ({
  serviceId: assertString(obj.serviceId),
  serviceName: assertString(obj.serviceName),
  serviceLocation: assertStringOrNull(obj.serviceLocation),
  staff: assertArray(obj.staff, assertString),
  startDate: assertStringOrNull(obj.startDate),
  endDate: assertStringOrNull(obj.endDate),
})

export const servicesReportNormaliser: AssertTypeFn<ServicesReportResponse> = (
  obj,
) => ({
  services: assertArray(obj.services, serviceReportItemNormaliser),
})

// --- Patients Weekly Report ---
const patientReportSlotNormaliser: AssertTypeFn<PatientReportSlotResponse> = (
  obj,
) => ({
  slotId: assertString(obj.slotId),
  time: assertString(obj.time),
  patient: assertStringOrNull(obj.patient),
  patientId: assertStringOrNull(obj.patientId),
  status: assertEnum(obj.status, SlotStatusType),
  staff: assertStringOrNull(obj.staff),
})

const patientReportDayNormaliser: AssertTypeFn<PatientReportDayResponse> = (
  obj,
) => ({
  date: assertString(obj.date),
  slots: assertArray(obj.slots, patientReportSlotNormaliser),
})

const patientReportServiceNormaliser: AssertTypeFn<
  PatientReportServiceResponse
> = (obj) => ({
  serviceId: assertString(obj.serviceId),
  serviceName: assertString(obj.serviceName),
  location: assertStringOrNull(obj.location),
  contacts: assertStringOrNull(obj.contacts),
  days: assertArray(obj.days, patientReportDayNormaliser),
})

export const patientsWeeklyReportNormaliser: AssertTypeFn<
  PatientsWeeklyReportResponse
> = (obj) => ({
  services: assertArray(obj.services, patientReportServiceNormaliser),
})

// --- Partner Patient Report ---
const partnerPatientSlotHistoryNormaliser: AssertTypeFn<
  PartnerPatientSlotHistoryResponse
> = (obj) => ({
  at: asStr(obj.at),
  status: assertEnum(obj.status, SlotStatusType),
  comment: assertStringOrNull(obj.comment),
  by: assertStringOrNull(obj.by),
  patientName: assertStringOrNull(obj.patientName),
  practitionerName: assertStringOrNull(obj.practitionerName),
})

export const partnerSlotHistoryListNormaliser: AssertTypeFn<
  PartnerPatientSlotHistoryResponse[]
> = (arr) => assertArr(arr, partnerPatientSlotHistoryNormaliser)

const partnerPatientAppointmentNormaliser: AssertTypeFn<
  PartnerPatientAppointmentResponse
> = (obj) => ({
  slotId: asStr(obj.slotId),
  partnerServiceId: asStr(obj.partnerServiceId),
  serviceName: assertStringOrNull(obj.serviceName),
  date: asStr(obj.date),
  time: asStr(obj.time),
  status: assertEnum(obj.status, SlotStatusType),
  practitionerName: assertStringOrNull(obj.practitionerName),
  history: assertArr(obj.history, partnerPatientSlotHistoryNormaliser),
})

export const partnerPatientReportNormaliser: AssertTypeFn<
  PartnerPatientReportResponse
> = (obj) => ({
  firstName: assertStringOrNull(obj.firstName),
  secondName: assertStringOrNull(obj.secondName),
  email: asStr(obj.email),
  phone: assertStringOrNull(obj.phone),
  dateOfBirth: assertStringOrNull(obj.dateOfBirth),
  accountCreatedAt: assertStringOrNull(obj.accountCreatedAt),
  appAccess: assertEnum(obj.appAccess, AppAccessStatus),
  appointments: assertArr(
    obj.appointments,
    partnerPatientAppointmentNormaliser,
  ),
})

export const rescheduleSlotResponseNormaliser: AssertTypeFn<
  RescheduleSlotResponse
> = (obj) => ({
  date: assertString(obj.date),
  partnerServiceSlotId: assertString(obj.partnerServiceSlotId),
  patientEmail: assertString(obj.patientEmail),
  patientFirstName: assertStringOrNull(obj.patientFirstName),
  patientId: assertString(obj.patientId),
  patientPhone: assertStringOrNull(obj.patientPhone),
  patientSecondName: assertStringOrNull(obj.patientSecondName),
  serviceName: assertString(obj.serviceName),
  time: assertString(obj.time),
})

export const partnerServiceSlotResponseNormaliser: AssertTypeFn<
  PartnerServiceSlotResponse
> = (obj) => ({
  id: assertString(obj.id),
  partnerServiceId: assertString(obj.partnerServiceId),
  practitionerId: assertStringOrNull(obj.practitionerId),
  patientId: assertStringOrNull(obj.patientId),
  appointmentId: assertStringOrNull(obj.appointmentId),
  startTime: assertString(obj.startTime),
  status: assertEnum(obj.status, SlotStatusType),
  statusNote: assertStringOrNull(obj.statusNote),
  statusUpdatedAt: assertStringOrNull(obj.statusUpdatedAt),
  statusUpdatedBy: assertStringOrNull(obj.statusUpdatedBy),
})

export const emailTemplateResponseNormaliser: AssertTypeFn<
  EmailTemplateResponse
> = (obj) => ({
  subject: assertString(obj.subject),
  section1: assertString(obj.section1),
  section2: assertString(obj.section2),
})
